import ArchiveIcon from '@mui/icons-material/Archive'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import StopIcon from '@mui/icons-material/Stop'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { alpha, type Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import PopupEditableText from '@/app/component/atom/popup-editable-text'
import CustomTooltip from '@/app/component/atom/tooltip'
import CampaignStatus from '@/app/module/campaigns/campaign-status'
import CampaignSidebar from '@/app/module/campaigns/component/campaign-sidebar'
import AnalyticsTab from '@/app/module/campaigns/component/item/summary/analytics-tab'
import OverviewTab from '@/app/module/campaigns/component/item/summary/overview-tab'
import CampaignSubscriptions from '@/app/module/campaigns/component/subscriptions'
import { getCampaignTypeLabel } from '@/app/module/campaigns/definitions'
import { type CampaignItem, type CampaignSummary, CampaignTemplateType } from '@/app/module/campaigns/types'
import { type CountryTimezone, type Timezones } from '@/app/types'

import { type CampaignMode, type DashboardCampaign, type DashboardData, type DashboardTab } from './types'

export type DashboardActionLoading = {
  abortCampaign?: boolean
  archiveCampaign?: boolean
  campaign?: boolean
  duplicateCampaign?: boolean
  publishCampaign?: boolean
  setCampaignOpen?: boolean
  unsubscribeAllContacts?: boolean
}

type Props = {
  activeTab: DashboardTab
  actionLoading?: DashboardActionLoading
  campaign: DashboardCampaign
  campaignItem: CampaignItem
  campaignSummary?: CampaignSummary
  countriesTimezones: CountryTimezone[]
  countryCode: string
  data: DashboardData
  itemId: number
  orgId: number
  refreshing: boolean
  timezone: string
  timezones: Timezones
  onAbortCampaign: () => void
  onAnalytics: () => void
  onArchiveCampaign: () => void
  onBack: () => void
  onCampaignIdCopyFailed: () => void
  onCampaignIdCopied: () => void
  onCampaignNameSubmit: (name: string) => Promise<void>
  onDownloadReport: () => void
  onDuplicateCampaign: () => void
  onEditCampaign: () => void
  onOverview: () => void
  onPublishCampaign: () => void
  onRefresh: () => void
  onReturnToDraft: () => void
  onSetCampaignOpen: () => void
  onSubscriptions: () => void
  onUnsubscribeAllContacts: () => void
}

const defaultActionLoading: Required<DashboardActionLoading> = {
  abortCampaign: false,
  archiveCampaign: false,
  campaign: false,
  duplicateCampaign: false,
  publishCampaign: false,
  setCampaignOpen: false,
  unsubscribeAllContacts: false,
}

const rightSidebarColumnWidth = 280
const dashboardContentGridColumns = { xs: '1fr', lg: `minmax(0, 1fr) ${rightSidebarColumnWidth}px` }
const dashboardHeaderGridColumns = { xs: '1fr', lg: `minmax(0, 1fr) auto ${rightSidebarColumnWidth}px` }
const unavailableContentSx = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minHeight: 320,
  p: { xs: 3, sm: 6 },
  textAlign: 'center',
  '&:last-child': { pb: { xs: 3, sm: 6 } },
} as const
export const ANALYTICS_UNAVAILABLE_TEXT = 'Analytics are unavailable until the campaign is Live'
export const SUBSCRIPTIONS_UNAVAILABLE_TEXT = 'Subscriptions are unavailable until the campaign is Live'
const noOp = () => undefined

const Dashboard: React.FC<Props> = ({
  activeTab,
  actionLoading = defaultActionLoading,
  campaign,
  campaignItem,
  campaignSummary,
  countriesTimezones,
  countryCode,
  data,
  itemId,
  orgId,
  refreshing,
  timezone,
  timezones,
  onAbortCampaign,
  onAnalytics,
  onArchiveCampaign,
  onBack,
  onCampaignIdCopyFailed,
  onCampaignIdCopied,
  onCampaignNameSubmit,
  onDownloadReport,
  onDuplicateCampaign,
  onEditCampaign,
  onOverview,
  onPublishCampaign,
  onRefresh,
  onReturnToDraft,
  onSetCampaignOpen,
  onSubscriptions,
  onUnsubscribeAllContacts,
}) => {
  const [campaignName, setCampaignName] = React.useState(campaign.name)
  const mode = campaign.status
  const isDraft = mode === 'draft'
  const isRegistering = mode === 'scheduled'
  const sidebarOptions = React.useMemo(
    () => [
      {
        active: activeTab === 'overview',
        icon: 'insert_chart',
        label: 'Overview',
        name: 'overview',
        onClick: activeTab === 'overview' ? noOp : onOverview,
      },
      {
        active: activeTab === 'analytics',
        icon: 'pie_chart',
        label: 'Analytics',
        name: 'analytics',
        onClick: activeTab === 'analytics' ? noOp : onAnalytics,
      },
      ...(!campaign.aborted
        ? [
            {
              active: activeTab === 'subscriptions',
              icon: 'people',
              label: 'Subscriptions',
              name: 'subscriptions',
              onClick: activeTab === 'subscriptions' ? noOp : onSubscriptions,
            },
          ]
        : []),
    ],
    [activeTab, campaign.aborted, onAnalytics, onOverview, onSubscriptions],
  )

  React.useEffect(() => {
    setCampaignName(campaign.name)
  }, [campaign.name])

  const handleCampaignNameSubmit = React.useCallback(
    async (name: string) => {
      await onCampaignNameSubmit(name)
      setCampaignName(name)
    },
    [onCampaignNameSubmit],
  )

  const handleCopyCampaignId = React.useCallback(async () => {
    if (!navigator.clipboard) {
      onCampaignIdCopyFailed()
      return
    }

    try {
      await navigator.clipboard.writeText(String(campaign.id))
      onCampaignIdCopied()
    } catch {
      onCampaignIdCopyFailed()
    }
  }, [campaign.id, onCampaignIdCopied, onCampaignIdCopyFailed])

  return (
    <Box
      sx={{
        bgcolor: 'grey.100',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: 'calc(100vh - 64px)',
        justifyContent: 'flex-start',
      }}
    >
      <CampaignSidebar campaignName={campaignName} options={sidebarOptions} onBack={onBack} />
      <Box
        id="scrollable-area"
        sx={{
          flex: 1,
          height: { md: 'calc(100vh - 64px)' },
          margin: 'auto',
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          position: 'relative',
          width: { xs: '100%', md: 'auto' },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Card
            data-testid="campaign-header-card"
            variant="outlined"
            sx={{
              borderRadius: 0.5,
              display: 'grid',
              gridTemplateColumns: dashboardHeaderGridColumns,
              width: '100%',
            }}
          >
            <Box
              data-testid="campaign-title-section"
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                minWidth: 0,
                px: { xs: 2, md: 3 },
                py: 2,
              }}
            >
              <PopupEditableText
                maxLength={200}
                text={campaignName}
                title="Edit campaign name"
                onSubmit={handleCampaignNameSubmit}
              >
                <Typography component="h1" variant="h6" sx={{ fontWeight: 700 }}>
                  {campaignName}
                </Typography>
              </PopupEditableText>
              {activeTab === 'overview' && (
                <Box sx={{ flexShrink: 0 }}>
                  <CustomTooltip title="Refresh overview">
                    <span>
                      <IconButton aria-label="Refresh overview" disabled={refreshing} size="small" onClick={onRefresh}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Box>
              )}
            </Box>
            <Divider data-testid="campaign-header-divider" sx={{ display: { xs: 'block', lg: 'none' } }} />
            <Box
              data-testid="campaign-header-divider-inset"
              sx={{
                display: { xs: 'none', lg: 'flex' },
                py: 1.5,
              }}
            >
              <Divider flexItem data-testid="campaign-header-divider" orientation="vertical" />
            </Box>
            <CampaignIdSection campaignId={campaign.id} onCopy={handleCopyCampaignId} />
          </Card>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 0.5,
              mt: 1,
              px: { xs: 2, md: 3 },
              py: 1.25,
            }}
          >
            <StatusLabel aborted={campaign.aborted} archived={campaign.archived} mode={mode} />
            <TypeLabel label={getCampaignTypeLabel(campaign.type)} />
            {!campaign.aborted && <SubscriptionStatusLabel open={campaign.launched && campaign.open} />}
          </Stack>

          <Box
            data-testid="campaign-dashboard-content"
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: activeTab === 'overview' ? dashboardContentGridColumns : 'minmax(0, 1fr)',
              mt: 2,
              width: '100%',
            }}
          >
            {activeTab === 'overview' && (
              <>
                {campaign.launched && (
                  <Box data-testid="campaign-overview-content" sx={{ minWidth: 0 }}>
                    <OverviewTab item={campaignItem} itemId={itemId} itemSummary={campaignSummary} />
                  </Box>
                )}
                {!campaign.launched && isRegistering && <RegistrationInProgress />}
                {!campaign.launched && isDraft && (
                  <Stack spacing={2}>
                    <CampaignStatus errorMessages={data.errors} isValid={data.errors.length === 0} published={false} />
                  </Stack>
                )}

                <ActionPanel
                  actionLoading={actionLoading}
                  campaign={campaign}
                  data={data}
                  onAbortCampaign={onAbortCampaign}
                  onArchiveCampaign={onArchiveCampaign}
                  onDownloadReport={onDownloadReport}
                  onDuplicateCampaign={onDuplicateCampaign}
                  onEditCampaign={onEditCampaign}
                  onPublishCampaign={onPublishCampaign}
                  onReturnToDraft={onReturnToDraft}
                  onSetCampaignOpen={onSetCampaignOpen}
                  onUnsubscribeAllContacts={onUnsubscribeAllContacts}
                />
              </>
            )}
            {activeTab === 'analytics' && (
              <Box data-testid="campaign-analytics-content" sx={{ minWidth: 0 }}>
                {campaign.launched ? (
                  <AnalyticsTab item={campaignItem} itemSummary={campaignSummary} />
                ) : (
                  <Card>
                    <CardContent data-testid="campaign-analytics-unavailable" sx={unavailableContentSx}>
                      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                        {ANALYTICS_UNAVAILABLE_TEXT}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
            {activeTab === 'subscriptions' && !campaign.aborted && (
              <Card data-testid="campaign-subscriptions-content" sx={{ minWidth: 0 }}>
                {campaign.launched ? (
                  <CampaignSubscriptions
                    countriesTimezones={countriesTimezones}
                    countryCode={countryCode}
                    item={campaignItem}
                    itemId={itemId}
                    orgId={orgId}
                    timezone={timezone}
                    timezones={timezones}
                  />
                ) : (
                  <CardContent data-testid="campaign-subscriptions-unavailable" sx={unavailableContentSx}>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                      {SUBSCRIPTIONS_UNAVAILABLE_TEXT}
                    </Typography>
                  </CardContent>
                )}
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

type CampaignIdBoxProps = {
  campaignId: number | string
  onCopy: () => Promise<void>
}

const CampaignIdSection: React.FC<CampaignIdBoxProps> = ({ campaignId, onCopy }) => (
  <Box
    data-testid="campaign-id-section"
    sx={{
      alignItems: 'center',
      display: 'flex',
      gap: 1.5,
      justifyContent: 'space-between',
      minHeight: 64,
      minWidth: 0,
      px: 2,
      py: 1.25,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography color="text.secondary" fontSize={12}>
        Campaign ID:
      </Typography>
      <Typography
        fontSize={14}
        fontWeight={700}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {campaignId}
      </Typography>
    </Box>
    <Button
      size="small"
      startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
      sx={{ cursor: 'pointer', flexShrink: 0, minWidth: 0, px: 0.75, py: 0.25 }}
      onClick={onCopy}
    >
      Copy
    </Button>
  </Box>
)

type StatusLabelProps = {
  aborted: boolean
  archived: boolean
  mode: CampaignMode
}

type PaletteValue = string | ((theme: Theme) => string)

const StatusLabel: React.FC<StatusLabelProps> = ({ aborted, archived, mode }) => {
  const isDraft = mode === 'draft'
  const isScheduled = mode === 'scheduled'
  const isStopped = aborted
  const labelSuffix = archived ? ' (Archived)' : ''
  let label = 'Live'
  let backgroundColor: PaletteValue = (theme) => alpha(theme.palette.success.main, 0.12)
  let color: PaletteValue = 'success.main'
  let dotColor: PaletteValue = 'success.main'

  if (isDraft) {
    label = 'Draft'
    backgroundColor = (theme) => alpha(theme.palette.warning.main, 0.16)
    color = 'warning.dark'
    dotColor = 'warning.main'
  }

  if (isScheduled) {
    label = 'Registering'
    backgroundColor = (theme) => alpha(theme.palette.primary.main, 0.12)
    color = 'primary.dark'
    dotColor = 'primary.main'
  }

  if (isStopped) {
    label = 'Stopped'
    backgroundColor = 'action.hover'
    color = 'text.secondary'
    dotColor = 'text.disabled'
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: backgroundColor,
        borderRadius: 999,
        color,
        display: 'inline-flex',
        fontSize: 12,
        fontWeight: 700,
        gap: 0.75,
        lineHeight: 1,
        minHeight: 24,
        px: 1.25,
        textTransform: 'uppercase',
      }}
    >
      {isScheduled ? (
        <WhatsAppIcon sx={{ color: dotColor, fontSize: 15 }} />
      ) : (
        <Box
          sx={{
            bgcolor: dotColor,
            borderRadius: '50%',
            height: 8,
            width: 8,
          }}
        />
      )}
      {label}
      {labelSuffix}
    </Box>
  )
}

type TypeLabelProps = {
  label: string
}

const TypeLabel: React.FC<TypeLabelProps> = ({ label }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'action.hover',
      borderRadius: 0.5,
      display: 'inline-flex',
      minHeight: 22,
      px: 1,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 900,
        letterSpacing: 0,
        lineHeight: 1,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
  </Box>
)

type SubscriptionStatusLabelProps = {
  open: boolean
}

const SubscriptionStatusLabel: React.FC<SubscriptionStatusLabelProps> = ({ open }) => {
  const IconComponent = open ? LockOpenIcon : LockIcon

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: open ? (theme) => alpha(theme.palette.success.main, 0.1) : 'action.hover',
        borderRadius: 0.5,
        color: open ? 'success.dark' : 'text.secondary',
        display: 'inline-flex',
        gap: 0.5,
        minHeight: 22,
        px: 1,
      }}
    >
      <IconComponent sx={{ fontSize: 15 }} />
      <Typography
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: 0,
          lineHeight: 1,
          textTransform: 'uppercase',
        }}
      >
        {open ? 'Open' : 'Closed'}
      </Typography>
    </Box>
  )
}

type ActionPanelProps = {
  actionLoading: DashboardActionLoading
  campaign: DashboardCampaign
  data: DashboardData
  onAbortCampaign: () => void
  onArchiveCampaign: () => void
  onDownloadReport: () => void
  onDuplicateCampaign: () => void
  onEditCampaign: () => void
  onPublishCampaign: () => void
  onReturnToDraft: () => void
  onSetCampaignOpen: () => void
  onUnsubscribeAllContacts: () => void
}

const ActionPanel: React.FC<ActionPanelProps> = ({
  actionLoading,
  campaign,
  data,
  onAbortCampaign,
  onArchiveCampaign,
  onDownloadReport,
  onDuplicateCampaign,
  onEditCampaign,
  onPublishCampaign,
  onReturnToDraft,
  onSetCampaignOpen,
  onUnsubscribeAllContacts,
}) => {
  const { aborted, archived, launched, open, status, type } = campaign
  const hasValidationErrors = data.errors.length > 0
  const isChatbot = type === CampaignTemplateType.Chatbot
  const publishDisabled = hasValidationErrors || isChatbot
  const publishDisabledReason = isChatbot
    ? 'Set chatbot campaigns live from the chatbot editor.'
    : 'Fix validation errors before setting this campaign live.'
  const aborting = Boolean(actionLoading.abortCampaign)
  const archiving = Boolean(actionLoading.archiveCampaign)
  const campLoading = Boolean(actionLoading.campaign)
  const duplicating = Boolean(actionLoading.duplicateCampaign)
  const publishing = Boolean(actionLoading.publishCampaign)
  const settingCampaignOpen = Boolean(actionLoading.setCampaignOpen)
  const unsubscribingAllContacts = Boolean(actionLoading.unsubscribeAllContacts)
  const drafting = status === 'draft'
  const editDisabledReason = aborted ? 'Stopped campaigns cannot be edited.' : ''
  const liveOnlyDisabledReason = launched && !aborted ? '' : 'Available only for live campaigns.'
  const registering = status === 'scheduled'
  const subscriptionsActionLabel = open ? 'Disable new subscriptions' : 'Enable new subscriptions'

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: { lg: 640 }, order: { xs: -1, lg: 0 } }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
        <TooltipButton
          color="primary"
          disabled={campLoading || aborted || (registering && publishing)}
          disabledReason={editDisabledReason}
          loading={registering && publishing}
          startIcon={<EditIcon />}
          variant="contained"
          onClick={registering ? onReturnToDraft : onEditCampaign}
        >
          {registering ? 'Return to draft' : 'Edit campaign'}
        </TooltipButton>

        {drafting && (
          <TooltipButton
            color="primary"
            disabled={campLoading || publishDisabled || publishing}
            disabledReason={publishDisabledReason}
            loading={publishing}
            startIcon={<PowerSettingsNewIcon />}
            variant="contained"
            onClick={onPublishCampaign}
          >
            Set Live
          </TooltipButton>
        )}

        <TooltipButton
          disabled={campLoading || !launched}
          disabledReason="Reports are only available for campaigns that have gone live."
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={onDownloadReport}
        >
          Download report
        </TooltipButton>

        <Divider />

        <Stack spacing={1}>
          <TooltipButton
            disabled={campLoading || duplicating}
            disabledReason=""
            loading={duplicating}
            startIcon={<ContentCopyIcon />}
            textAlign="start"
            onClick={onDuplicateCampaign}
          >
            Duplicate campaign
          </TooltipButton>
          <TooltipButton
            disabled={campLoading || archiving}
            disabledReason=""
            loading={archiving}
            startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
            textAlign="start"
            onClick={onArchiveCampaign}
          >
            {archived ? 'Unarchive campaign' : 'Archive campaign'}
          </TooltipButton>
          <TooltipButton
            color="primary"
            disabled={campLoading || !launched || aborted || unsubscribingAllContacts}
            disabledReason={liveOnlyDisabledReason}
            loading={unsubscribingAllContacts}
            startIcon={<UnsubscribeIcon />}
            textAlign="start"
            onClick={onUnsubscribeAllContacts}
          >
            Unsubscribe all
          </TooltipButton>
          <TooltipButton
            disabled={campLoading || settingCampaignOpen || aborted || !launched}
            disabledReason={liveOnlyDisabledReason}
            loading={settingCampaignOpen}
            startIcon={open ? <LockIcon /> : <LockOpenIcon />}
            textAlign="start"
            onClick={onSetCampaignOpen}
          >
            {subscriptionsActionLabel}
          </TooltipButton>
        </Stack>

        {!aborted && (
          <Box sx={{ mt: 'auto' }}>
            <TooltipButton
              color="error"
              disabled={campLoading || !launched || aborting}
              disabledReason={liveOnlyDisabledReason}
              loading={aborting}
              startIcon={<StopIcon />}
              variant="outlined"
              onClick={onAbortCampaign}
            >
              Stop campaign
            </TooltipButton>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const RegistrationInProgress: React.FC = () => (
  <Card sx={{ minHeight: 320 }}>
    <CardContent
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        minHeight: 320,
        textAlign: 'center',
      }}
    >
      <CircularProgress />
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
        Registering campaign
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 560 }}>
        Registration usually takes a few minutes, but it can take many hours. If registration is rejected, the campaign
        launch will fail.
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 560 }}>
        Subscriptions are unavailable until the campaign is Live.
      </Typography>
    </CardContent>
  </Card>
)

type TooltipButtonProps = React.ComponentProps<typeof Button> & {
  disabledReason: string
  textAlign?: 'center' | 'start'
}

const TooltipButton: React.FC<TooltipButtonProps> = ({
  children,
  disabled,
  disabledReason,
  loading,
  sx,
  textAlign,
  ...props
}) => (
  <CustomTooltip title={disabled && !loading ? disabledReason : ''}>
    <span style={{ display: 'block', width: '100%' }}>
      <Button
        fullWidth
        disabled={disabled}
        loading={loading}
        sx={[
          {
            cursor: disabled ? 'default' : 'pointer',
            justifyContent: textAlign === 'start' ? 'flex-start' : undefined,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      >
        {children}
      </Button>
    </span>
  </CustomTooltip>
)

export default Dashboard
